import { adminNavItems } from './adminNav';

/** Path prefixes → module id (sidebar section). */
const PATH_MODULE_RULES = [
  { prefix: '/admin/assign-users', ownerOnly: true },
  { prefix: '/admin/dashboard', module: 'home' },
  { prefix: '/admin/search', module: 'search' },
  { prefix: '/admin/products', module: 'products' },
  { prefix: '/admin/upload-tracking', module: 'upload-tracking' },
  { prefix: '/admin/revenue-calculator', module: 'revenue-calculator' },
  { prefix: '/admin/analytics', module: 'analytics' },
  { prefix: '/admin/orders', module: 'orders' },
  { prefix: '/admin/unshipped-orders', module: 'unshipped' },
  { prefix: '/admin/procurement', module: 'procurement' },
  { prefix: '/admin/fulfillment', module: 'fulfillment' },
  { prefix: '/admin/customer-service', module: 'customer-service' },
  { prefix: '/admin/inventory', module: 'inventory' },
  { prefix: '/admin/invoices', module: 'invoices' },
  { prefix: '/admin/reports', module: 'reports' },
];

export function isWorkspaceUser(user) {
  return user?.role === 'workspace_user';
}

export function isSellerAdmin(user) {
  return user?.role === 'admin';
}

/** Admins/super_admins get every module; staff get their assigned list. */
export function getUserModules(user) {
  if (!user) {
    return [];
  }

  if (user.role === 'admin' || user.role === 'super_admin') {
    return adminNavItems.map((item) => item.id);
  }

  if (Array.isArray(user.modules)) {
    return user.modules;
  }

  return [];
}

export function userHasModule(user, moduleId) {
  if (!moduleId) {
    return true;
  }
  return getUserModules(user).includes(moduleId);
}

export function filterNavItemsByModules(navItems, user) {
  if (!isWorkspaceUser(user)) {
    return navItems;
  }

  const allowed = new Set(getUserModules(user));
  if (
    ['procurement', 'orders', 'unshipped', 'fulfillment', 'customer-service'].some((id) =>
      allowed.has(id)
    )
  ) {
    allowed.add('search');
  }
  return navItems.filter((item) => allowed.has(item.id));
}

export function resolvePathAccess(pathname) {
  const path = pathname.startsWith('/admin')
    ? pathname
    : `/admin${pathname === '/' ? '/dashboard' : pathname}`;

  for (const rule of PATH_MODULE_RULES) {
    if (path === rule.prefix || path.startsWith(`${rule.prefix}/`)) {
      return rule;
    }
  }

  return { prefix: path, module: null };
}

export function canAccessAdminPath(user, pathname) {
  if (!user) {
    return false;
  }

  if (user.role === 'admin' || user.role === 'super_admin') {
    return true;
  }

  if (!isWorkspaceUser(user)) {
    return false;
  }

  const rule = resolvePathAccess(pathname);

  if (rule.ownerOnly) {
    return false;
  }

  if (rule.module === 'search') {
    return ['procurement', 'orders', 'unshipped', 'fulfillment', 'customer-service'].some((id) =>
      userHasModule(user, id)
    );
  }

  if (!rule.module) {
    return false;
  }

  return userHasModule(user, rule.module);
}

/** First allowed nav path for this user (fallback dashboard if admin). */
export function getDefaultAdminPath(user) {
  if (!isWorkspaceUser(user)) {
    return '/admin/dashboard';
  }

  const allowed = filterNavItemsByModules(adminNavItems, user);
  if (allowed.length) {
    return allowed[0].to;
  }

  return '/admin/no-access';
}
